// src/hooks/useOpsViews.ts
//
// React data hooks for the three standalone ops list views that outlived the
// pre-pivot stack: /kb-updates, /health and /prospects. Story 4.10 deleted
// useResolutions.ts with the Morning Queue and Escalations hooks; these three
// moved here verbatim so the surviving screens don't keep a retired module
// alive.
//
// Plain useState/useEffect/useCallback — no React Query, per the repo pattern.
// All data access goes through opsViewsService. None of the three subscribes to
// postgres_changes: yak_kb_updates has no writer left, and health/prospects are
// low-churn, refetch-on-demand screens.

import { useCallback, useEffect, useState } from 'react'
import { opsViewsService } from '../services/opsViewsService'
import type { YakCustomerHealth, YakKbUpdate, YakProspect } from '../types/yak'

interface UseKbUpdatesReturn {
  kbUpdates: YakKbUpdate[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
  markComplete: (id: string) => Promise<void>
}

export function useKbUpdates(): UseKbUpdatesReturn {
  const [kbUpdates, setKbUpdates] = useState<YakKbUpdate[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchKbUpdates = useCallback(async (silent = false) => {
    try {
      if (!silent) setLoading(true)
      setError(null)
      setKbUpdates(await opsViewsService.listKbUpdates())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load KB updates')
    } finally {
      if (!silent) setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchKbUpdates()
  }, [fetchKbUpdates])

  const markComplete = useCallback(
    async (id: string) => {
      await opsViewsService.markKbUpdateComplete(id)
      await fetchKbUpdates(true)
    },
    [fetchKbUpdates]
  )

  return {
    kbUpdates,
    loading,
    error,
    refetch: () => fetchKbUpdates(),
    markComplete,
  }
}

interface UseCustomerHealthReturn {
  health: YakCustomerHealth[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useCustomerHealth(): UseCustomerHealthReturn {
  const [health, setHealth] = useState<YakCustomerHealth[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchHealth = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      setHealth(await opsViewsService.customerHealth())
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load customer health'
      )
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchHealth()
  }, [fetchHealth])

  return { health, loading, error, refetch: fetchHealth }
}

interface UseProspectsReturn {
  prospects: YakProspect[]
  loading: boolean
  error: string | null
  refetch: () => Promise<void>
}

export function useProspects(): UseProspectsReturn {
  const [prospects, setProspects] = useState<YakProspect[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchProspects = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)
      setProspects(await opsViewsService.listProspects())
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load prospects')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchProspects()
  }, [fetchProspects])

  return { prospects, loading, error, refetch: fetchProspects }
}
